import { getVectorTileApi } from '@/components/Map/Utils/api';
import { DEFAULT_BOUNDARY_COLOR, DEFAULT_LABEL_COLOR, MAP_RULER_COLORS } from './colors';
import {
  DEFAULT_CCUS_LAYERS,
  DEFAULT_OG_LAYERS,
  defaultLabelLayerFilter,
  MAP_LAYER_IDS,
  MAP_LAYER_OPTIONS,
  MAP_SOURCE_IDS,
  MAP_TILESET_TYPES,
  WELLIGENCE_SOURCE,
} from './constants';
import { MAP_LAYER_TYPES } from './layerTypes';
import { getMapLabelTextColors, getMapOptions } from './mapStyles';
import { LIGHT_MAP_STYLE } from './mapStyleTypes';
import { mapLayerMatch } from './utils';

/* LAYER LABELS */
// List of layers that require labels
export const layerNeedsLabels = layerName => {
  if (layerName.includes('-labels')) return false; // if it's already a label layer

  const dynamicMapLayers = [
    MAP_LAYER_TYPES.assets,
    MAP_LAYER_TYPES.blocks,
    MAP_LAYER_TYPES.leases,
    MAP_LAYER_TYPES.fields,
    MAP_LAYER_TYPES.storageSites,
    MAP_LAYER_TYPES.wells,
  ];

  if (dynamicMapLayers.includes(layerName)) {
    return true;
  }

  return (
    mapLayerMatch(layerName, [
      MAP_LAYER_TYPES.blocks,
      MAP_LAYER_TYPES.assets,
      MAP_LAYER_TYPES.leases,
      MAP_LAYER_TYPES.fields,
      MAP_LAYER_TYPES.storageSites,
      MAP_LAYER_TYPES.wells,
    ]) || mapLayerMatch(layerName, ['storage_sites', 'ccus_projects', 'co2_emitters'])
  );
};

// Build ID for entity type labels layer
export const buildLabelsLayerId = layerName => {
  if (mapLayerMatch(layerName, [MAP_LAYER_TYPES.basins])) {
    // For basins, we need to use a specific ID, since the layer name is coming from the source in Mapbox
    // and it doesn't match the expected format.
    // If you check Mapbox Studio, in the Satellite source for instance, the layer ID is 'basin_labels'.
    return MAP_LAYER_IDS.basinsLabels;
  }

  return `${layerName}-labels`;
};

/* LAYER OPERATORS */
// List of layers that require operator labels
export const layerNeedsOperatorLabels = layerName => {
  const dynamicMapLayers = [MAP_LAYER_TYPES.blocks, MAP_LAYER_TYPES.assets, MAP_LAYER_TYPES.leases];

  if (dynamicMapLayers.includes(layerName)) {
    return true;
  }

  return (
    mapLayerMatch(layerName, [
      MAP_LAYER_TYPES.blocks,
      MAP_LAYER_TYPES.assets,
      MAP_LAYER_TYPES.leases,
    ]) || mapLayerMatch(layerName, ['storage_sites', 'ccus_projects', 'co2_emitters'])
  );
};

// Add labels layer to map
const addLabelsLayer = (map, layerId, source, sourceLayer, layerIsHidden = false, style) => {
  let opts = { ...MAP_LAYER_OPTIONS.label };
  // The well labels have different properties than the other labels:
  if (mapLayerMatch(sourceLayer, [MAP_LAYER_TYPES.wells, MAP_LAYER_TYPES.eaWells])) {
    opts = { ...MAP_LAYER_OPTIONS.wellLabel };
  } else if (style === LIGHT_MAP_STYLE.value) {
    // When in light mode, assets, blocks, leases, and fields will have different properties
    if (
      mapLayerMatch(sourceLayer, [
        MAP_LAYER_TYPES.assets,
        MAP_LAYER_TYPES.blocks,
        MAP_LAYER_TYPES.leases,
      ])
    ) {
      opts = { ...MAP_LAYER_OPTIONS.lightBlockLabel };
    } else if (mapLayerMatch(sourceLayer, [MAP_LAYER_TYPES.fields])) {
      opts = { ...MAP_LAYER_OPTIONS.lightFieldLabel };
    }
  }

  if (layerIsHidden) {
    opts.layout = { ...opts.layout, visibility: 'none' };
  }

  const textColor = getMapLabelTextColors(sourceLayer, style);
  const layer = {
    ...opts,
    id: layerId,
    source: source,
    paint: {
      ...opts.paint,
      'text-color': textColor,
    },
  };

  layer.filter = defaultLabelLayerFilter;

  // Don't add a source-layer property for the E&A wells, since
  // it's not needed and it prevents the labels from showing up:
  if (!mapLayerMatch(sourceLayer, [MAP_LAYER_TYPES.eaWells])) {
    layer['source-layer'] = sourceLayer;
  }

  try {
    map.addLayer(layer);
  } catch (error) {
    console.error(`Error adding label layer ${layerId}:`, error);
  }
};

// Hide basins layer
export const hideBasinsLayer = map => {
  map.setLayoutProperty(MAP_LAYER_IDS.basins, 'visibility', 'none');
  map.setLayoutProperty(MAP_LAYER_IDS.basinsLabels, 'visibility', 'none');
};

/* ADDITIONAL LAYERS */
// Country boundaries layer
const addCountryBoundariesLayer = map => {
  // use mapbox layer for country boundaries

  // Fill layer is used to color in the country:
  map.addLayer(MAP_LAYER_OPTIONS.countryFillBoundary, 'country-label');

  // Line layer is used to highlight countries:
  map.addLayer(MAP_LAYER_OPTIONS.countryLineBoundary);

  // Highlight layer is used to highlight the selected country
  map.addLayer(MAP_LAYER_OPTIONS.countrySelectedBoundary);

  // add our custom layer for custom regions
  map.addLayer(MAP_LAYER_OPTIONS.customRegionFillBoundary);
  map.addLayer(MAP_LAYER_OPTIONS.customRegionLineBoundary);
  map.addLayer(MAP_LAYER_OPTIONS.customRegionSelectedBoundary);
  // add labels for our custom regions
  map.addLayer(MAP_LAYER_OPTIONS.customRegionLabel);
};

// LNG Facilities layer
const addLngFacilitiesLayer = map => {
  // LNG Facilities - icons layer:
  map.addSource(MAP_SOURCE_IDS.lngFacilities, {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  });
  map.addLayer(MAP_LAYER_OPTIONS.lngFacility);
};

// Pipeline Networks layer
const addPipelineNetworksLayer = map => {
  // Pipeline Networks
  map.addSource(MAP_SOURCE_IDS.pipelineNetworks, {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  });
  map.addLayer(MAP_LAYER_OPTIONS.pipelineNetwork);
};

// Screening layer
const addScreeningLayers = map => {
  // Screening - E&A wells
  try {
    map.addSource(MAP_SOURCE_IDS.eaWells, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
  } catch (_error) {
    // Error adding E&A wells source
  }

  try {
    // Create E&A wells layer with initial visibility set to 'none'
    const eaWellLayerOptions = {
      ...MAP_LAYER_OPTIONS.eaWell,
      layout: {
        visibility: 'none', // Start hidden
      },
    };
    map.addLayer(eaWellLayerOptions);
  } catch (_error) {
    // Error adding E&A wells layer
  }

  try {
    // Create E&A well labels layer with initial visibility set to 'none'
    const labelLayerOptions = {
      type: 'symbol',
      minzoom: 6,
      id: MAP_LAYER_IDS.eaWellLabels,
      source: MAP_SOURCE_IDS.eaWells,
      layout: {
        visibility: 'none', // Start hidden
        'icon-anchor': 'center',
        'icon-allow-overlap': false,
        'icon-ignore-placement': false,
        'text-field': ['get', 'name'],
        'text-font': ['Open Sans Semibold', 'Arial Unicode MS Bold'],
        'text-letter-spacing': 0.2,
        'text-size': 12,
        'text-anchor': 'center',
        'text-allow-overlap': false,
        'text-ignore-placement': false,
      },
      paint: {
        'text-halo-color': DEFAULT_BOUNDARY_COLOR,
        'text-halo-width': 1.5,
        'text-color': DEFAULT_LABEL_COLOR,
      },
    };
    map.addLayer(labelLayerOptions);
  } catch (_error) {
    // Error adding E&A well labels layer
  }

  // add screening source outline
  map.addSource(MAP_SOURCE_IDS.screening, {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
  });
  map.addLayer(MAP_LAYER_OPTIONS.screeningFill);
  map.addLayer(MAP_LAYER_OPTIONS.screeningOutline);

  // screening source cluster:
  map.addSource(MAP_SOURCE_IDS.screeningCluster, {
    type: 'geojson',
    data: {
      type: 'FeatureCollection',
      features: [],
    },
    cluster: true,
    clusterMaxZoom: 6,
    clusterRadius: 50,
  });

  // Add cluster layers and move them to the top
  map.addLayer(MAP_LAYER_OPTIONS.screeningCluster);
  map.addLayer(MAP_LAYER_OPTIONS.screeningClusterCount);

  // Move cluster layers to the top of the layer stack
  map.moveLayer(MAP_LAYER_OPTIONS.screeningCluster.id);
  map.moveLayer(MAP_LAYER_OPTIONS.screeningClusterCount.id);

  // Zoom to cluster on click:
  map.on('click', MAP_LAYER_IDS.screeningClusters, e => {
    const features = map.queryRenderedFeatures(e.point, {
      layers: [MAP_LAYER_IDS.screeningClusters],
    });

    // Only one cluster is selected at a time, so we can just use the first feature
    // in order to get the cluster id:
    const selectedFeature = features[0];
    const clusterId = selectedFeature.properties.cluster_id;
    map
      .getSource(MAP_SOURCE_IDS.screeningCluster)
      .getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return;

        // The zoom can be null When the cluster contains only one point, so we use a default zoom level
        const targetZoom = zoom || 8;

        map.easeTo({
          center: selectedFeature.geometry.coordinates,
          zoom: targetZoom,
        });
      });
  });
};

// Add additional layers to map
export const addAdditionalLayers = (map, layers) => {
  if (layers.includes(MAP_LAYER_TYPES.countryBoundaries)) addCountryBoundariesLayer(map);
  if (layers.includes(MAP_LAYER_TYPES.lng)) addLngFacilitiesLayer(map);
  if (layers.includes(MAP_LAYER_TYPES.screening)) addScreeningLayers(map);
  if (layers.includes(MAP_LAYER_TYPES.pipelineNetworks)) addPipelineNetworksLayer(map);
};

/* LAYER HIGHLIGHTING */
// List of layers that require highlighting
const layerNeedsHighlight = layerName => {
  // Extract the base layer name if it's country-prefixed

  // For DynamicMap, we're using the actual layer names, not regex patterns
  const highlightableLayers = [
    MAP_LAYER_TYPES.blocks,
    MAP_LAYER_TYPES.assets,
    MAP_LAYER_TYPES.leases,
    MAP_LAYER_TYPES.fields,
    MAP_LAYER_TYPES.formations,
    MAP_LAYER_TYPES.pipelines,
    MAP_LAYER_TYPES.pipelineSegments,
    MAP_LAYER_TYPES.storageSites,
    MAP_LAYER_TYPES.ccusProjects,
    MAP_LAYER_TYPES.co2Emitters,
  ];

  // First check if it's an exact match (for DynamicMap)
  if (highlightableLayers.includes(layerName)) {
    return true;
  }

  // Then check if it matches regex patterns (for StandardMap)
  return mapLayerMatch(layerName, [
    MAP_LAYER_TYPES.blocks,
    MAP_LAYER_TYPES.assets,
    MAP_LAYER_TYPES.leases,
    MAP_LAYER_TYPES.fields,
    MAP_LAYER_TYPES.formations,
    MAP_LAYER_TYPES.pipelines,
    MAP_LAYER_TYPES.pipelineSegments,
  ]);
};

// Build ID for entity type highlight layer
export const buildHighlightLayerId = layerName => {
  return `${layerName}-highlight`;
};

// Add highlight layer to map
const addHighlightLayer = (map, layer, source) => {
  map.addLayer({
    ...MAP_LAYER_OPTIONS.highlight,
    id: buildHighlightLayerId(layer),
    source,
    'source-layer': layer,
  });
};

// Filter layers to only include those that exist in the tileset
const getValidTilesetLayers = (tilesetType, requestedLayers) => {
  if (tilesetType === 'ccus') {
    // Return layers in the order specified by DEFAULT_CCUS_LAYERS
    return DEFAULT_CCUS_LAYERS.filter(layer => requestedLayers.includes(layer));
  } else {
    // Return layers in the order specified by DEFAULT_OG_LAYERS
    return DEFAULT_OG_LAYERS.filter(layer => requestedLayers.includes(layer));
  }
};

export const addDynamicLayers = ({
  map,
  hideLayers = [],
  initialMetric,
  initialSizeType,
  layers = null,
  enableEntityHighlight = false,
  source,
  style,
}) => {
  hideBasinsLayer(map);

  const tilesetType = map.getTilesetType();
  if (!map.getSource(WELLIGENCE_SOURCE)) {
    map.addSource(WELLIGENCE_SOURCE, {
      type: 'vector',
      tiles: [getVectorTileApi(map.getTilesetType(), source)],
    });
  }

  let layerList = layers;

  // If no layers are provided, use default layers based on tileset type
  if (!layerList) {
    layerList = tilesetType === MAP_TILESET_TYPES.ccus ? DEFAULT_CCUS_LAYERS : DEFAULT_OG_LAYERS;
  } else {
    // Filter layers to only include those that exist in the tileset
    layerList = getValidTilesetLayers(tilesetType, layerList);
  }

  // Filter out any undefined or null layers
  layerList = layerList.filter(layer => layer !== null);

  const labelLayers = [];
  const highlightLayers = [];

  layerList.forEach(layerId => {
    const layerIsHidden = hideLayers.includes(layerId);
    // Extract the base layer name for source-layer
    // Get the appropriate style options for this layer
    const styleOptions = getMapOptions(layerId, initialMetric, initialSizeType);

    const layerOptions = {
      id: layerId,
      source: WELLIGENCE_SOURCE,
      'source-layer': layerId, // Use the correct source layer name
      ...(styleOptions || { type: 'circle' }), // Use the style options if available, otherwise default to circle
      ...(layerIsHidden && { layout: { visibility: 'none' } }), // Set visibility to 'none' if layer is hidden
    };

    try {
      map.addLayer(layerOptions);
    } catch (error) {
      console.error(`Error adding layer ${layerId}:`, error);
    }

    // Check if this layer needs labels
    if (layerNeedsLabels(layerId)) {
      labelLayers.push({ layer: layerId, sourceLayer: layerId }); // Use layer instead of layer
    }

    // Add highlight layer if enabled and needed
    if (enableEntityHighlight && layerNeedsHighlight(layerId)) {
      highlightLayers.push({ layer: layerId, source: WELLIGENCE_SOURCE }); // Use layer instead of layer
    }
  });

  // Add highlight layers
  highlightLayers.forEach(({ layer, source }) => {
    addHighlightLayer(map, layer, source);
  });

  // Add label layers
  labelLayers.forEach(({ layer, sourceLayer }) => {
    const layerId = buildLabelsLayerId(layer);
    // Extract original layer name for hideLayers check (remove country prefix)
    const layerIsHidden = hideLayers.includes(layer);
    try {
      addLabelsLayer(map, layerId, WELLIGENCE_SOURCE, sourceLayer, layerIsHidden, style);
    } catch (error) {
      console.error('Error adding label layer:', layerId, error);
    }
  });

  // Add additional layers (e.g. country boundaries, LNG facilities, screening layers)
  addAdditionalLayers(map, layers);
};

// Add tileset layers to map
export const addLayers = (
  map,
  mapTilesetSources,
  layers,
  initialMetric,
  initialSizeType,
  hideLayers,
  enableEntityHighlight,
  style,
) => {
  hideBasinsLayer(map);

  mapTilesetSources?.forEach(source => {
    const { country, sourceId, layers: sourceLayers } = source;
    if (!sourceId) {
      return;
    }

    map.addSource(country, {
      type: 'vector',
      url: `mapbox://${sourceId}`,
    });

    const labelLayers = [];
    const highlightLayers = [];
    sourceLayers.forEach(layer => {
      if (mapLayerMatch(layer, layers)) {
        const opts = { ...getMapOptions(layer, initialMetric, initialSizeType) };

        if (opts) {
          // If layer is meant to be hidden (is toggled off but can be toggled on),
          // add visiblity 'none' to layout
          if (mapLayerMatch(layer, hideLayers)) {
            opts.layout = { ...opts.layout, visibility: 'none' };
          }

          map.addLayer({
            id: layer,
            source: country,
            'source-layer': layer,
            ...opts,
          });

          if (enableEntityHighlight && layerNeedsHighlight(layer)) {
            highlightLayers.push({ layer, country });
          }

          if (layerNeedsLabels(layer)) {
            labelLayers.push({ layer, country });
          }

          map.on('mouseenter', `${layer}`, () => {
            map.getCanvas().style.cursor = 'pointer';
          });

          map.on('mouseleave', `${layer}`, () => {
            map.getCanvas().style.cursor = '';
          });
        }
      }
    });

    highlightLayers.forEach(({ layer, country }) => {
      addHighlightLayer(map, layer, country);
    });

    labelLayers.forEach(({ layer, country }) => {
      const layerId = buildLabelsLayerId(layer);
      const layerIsHidden = mapLayerMatch(layer, hideLayers);
      addLabelsLayer(map, layerId, country, layer, layerIsHidden, style);
    });
  });
};

/* RULER LAYER */
// Add ruler context to map
export const addRulerContext = (map, geojson, style) => {
  map.addSource(MAP_SOURCE_IDS.geojson, {
    type: 'geojson',
    data: geojson,
  });
  // Add styles to the map
  map.addLayer({
    ...MAP_LAYER_OPTIONS.measureLine,
    paint: {
      ...MAP_LAYER_OPTIONS.measureLine.paint,
      'line-color': MAP_RULER_COLORS[style],
    },
  });
  map.addLayer(MAP_LAYER_OPTIONS.measurePoint);
  map.addLayer(MAP_LAYER_OPTIONS.distanceLabel);
};
